import apiClient from '../../../api/client';
import type {
  Project,
  ProjectDetails,
  PaginatedEntries,
  QualityResult,
  QualityInfo,
  SandboxStatus,
  DiffResult,
  Snapshot,
} from './types';

// ─── API Functions ────────────────────────────────────────────────────────────

export const fetchProjects = async (): Promise<Project[]> => {
  const res = await apiClient.get('/translations/projects?limit=200');
  return res.data.data;
};

export const fetchProjectDetails = async (
  slug: string,
): Promise<ProjectDetails> => {
  const res = await apiClient.get(`/translations/projects/${slug}`);
  return res.data;
};

export const fetchEntries = async (
  slug: string,
  ns: string,
  page: number,
  limit: number,
  search: string,
  sortBy: string,
  sortOrder: string,
  qualityLevel?: string,
  reviewState?: string,
): Promise<PaginatedEntries> => {
  const params: Record<string, string | number> = {
    page,
    limit,
    sortBy,
    sortOrder,
  };
  if (search.length >= 2) params.search = search;
  if (qualityLevel) params.qualityLevel = qualityLevel;
  if (reviewState) params.reviewState = reviewState;
  const res = await apiClient.get(
    `/translations/projects/${slug}/namespaces/${ns}/entries`,
    { params },
  );
  return res.data;
};

export const aiTranslate = async (
  text: string,
  projectSlug?: string,
  context?: string,
  targetLocales?: string[],
): Promise<{ translations: Record<string, string>; contextNeed: 'required' | 'useful' | 'none'; contextReason: string | null }> => {
  const body: Record<string, unknown> = { text, projectSlug };
  if (context?.trim()) body.context = context.trim();
  if (targetLocales?.length) body.targetLocales = targetLocales;
  const res = await apiClient.post('/translations/ai-translate', body);
  return res.data;
};

export const checkQuality = async (
  source: string,
  translation: string,
  locale: string,
  mode: 'translation_quality' | 'language_quality' = 'translation_quality',
  projectSlug?: string,
  context?: string,
): Promise<QualityResult> => {
  const res = await apiClient.post('/translations/ai-quality-check', {
    source,
    translation,
    locale,
    mode,
    projectSlug,
    ...(context?.trim() ? { context: context.trim() } : {}),
  });
  return res.data;
};

export const fetchSandboxStatus = async (
  slug: string,
): Promise<SandboxStatus> => {
  const res = await apiClient.get(
    `/translations/projects/${slug}/sandbox/status`,
  );
  return res.data;
};

export const fetchSandboxDiff = async (slug: string): Promise<DiffResult> => {
  const limit = 200;
  const firstRes = await apiClient.get(
    `/translations/projects/${slug}/sandbox/diff`,
    { params: { page: 1, limit } },
  );
  const firstPage = firstRes.data as DiffResult;

  const totalPages = firstPage.meta?.totalPages ?? 1;
  if (totalPages <= 1) return firstPage;

  const pageRequests: Promise<DiffResult>[] = [];
  for (let page = 2; page <= totalPages; page += 1) {
    pageRequests.push(
      apiClient
        .get(`/translations/projects/${slug}/sandbox/diff`, {
          params: { page, limit },
        })
        .then((res) => res.data as DiffResult),
    );
  }

  const restPages = await Promise.all(pageRequests);
  return {
    ...firstPage,
    entries: [firstPage.entries, ...restPages.map((page) => page.entries)].flat(),
    meta: firstPage.meta
      ? {
          ...firstPage.meta,
          page: 1,
          limit,
        }
      : undefined,
  };
};

export const fetchSnapshots = async (slug: string): Promise<Snapshot[]> => {
  const res = await apiClient.get(
    `/translations/projects/${slug}/sandbox/snapshots`,
  );
  return res.data;
};

export const fetchSandboxEntries = async (
  slug: string,
  ns: string,
  page: number,
  limit: number,
  search: string,
  sortBy: string,
  sortOrder: string,
  qualityLevel?: string,
  reviewState?: string,
): Promise<PaginatedEntries> => {
  const params: Record<string, string | number> = {
    page,
    limit,
    sortBy,
    sortOrder,
  };
  if (search.length >= 2) params.search = search;
  if (qualityLevel) params.qualityLevel = qualityLevel;
  if (reviewState) params.reviewState = reviewState;
  const res = await apiClient.get(
    `/translations/projects/${slug}/sandbox/namespaces/${ns}/entries`,
    { params },
  );
  return res.data;
};

export const createSandboxEntry = async (
  slug: string,
  ns: string,
  payload: { key: string; values: Record<string, string>; context?: string },
) => {
  const res = await apiClient.post(
    `/translations/projects/${slug}/sandbox/namespaces/${ns}/entries`,
    payload,
  );
  return res.data;
};

export const updateSandboxEntry = async (
  slug: string,
  ns: string,
  key: string,
  values: Record<string, string>,
  context?: string,
) => {
  const res = await apiClient.patch(
    `/translations/projects/${slug}/sandbox/namespaces/${ns}/entries/${encodeURIComponent(key)}`,
    { values, ...(context !== undefined ? { context } : {}) },
  );
  return res.data;
};

export const deleteSandboxEntry = async (
  slug: string,
  ns: string,
  key: string,
): Promise<void> => {
  await apiClient.delete(
    `/translations/projects/${slug}/sandbox/namespaces/${ns}/entries/${encodeURIComponent(key)}`,
  );
};

export const revertSandboxKey = async (
  slug: string,
  ns: string,
  key: string,
): Promise<void> => {
  await apiClient.post(
    `/translations/projects/${slug}/sandbox/namespaces/${ns}/entries/${encodeURIComponent(key)}/revert`,
  );
};

export const markSandboxExpected = async (
  slug: string,
  ns: string,
  key: string,
  locale: string,
): Promise<QualityInfo> => {
  const res = await apiClient.post<QualityInfo>(
    `/translations/projects/${slug}/sandbox/namespaces/${ns}/entries/${encodeURIComponent(key)}/locales/${locale}/mark-expected`,
  );
  return res.data;
};

export const unmarkSandboxExpected = async (
  slug: string,
  ns: string,
  key: string,
  locale: string,
): Promise<void> => {
  await apiClient.delete(
    `/translations/projects/${slug}/sandbox/namespaces/${ns}/entries/${encodeURIComponent(key)}/locales/${locale}/mark-expected`,
  );
};

export const promote = async (
  slug: string,
  keys: { namespace: string; key: string }[],
): Promise<{ snapshotId: string; promoted: number }> => {
  const res = await apiClient.post(
    `/translations/projects/${slug}/sandbox/promote`,
    { keys },
  );
  return res.data;
};
